import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { showToast } from '../context/ToastHandle';
import { getAccountType } from '../context/accountUtils';

const STORAGE_PREFIX = 'stickyCoreSettings:';

const getUserId = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return 'current';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.sub || 'current';
  } catch {
    return 'current';
  }
};

export const saveStickyCoreCache = (settings) => {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${getUserId()}`,
      JSON.stringify(settings)
    );
  } catch {}
};

export const loadStickyCoreCache = () => {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${getUserId()}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function StickyCoreSettings({ onOpen = null }) {
  const isUnlimited = getAccountType() === 'unlimited';
  const [settings, setSettings] = useState(() => loadStickyCoreCache() || {
    enabled: false,
    board_id: null,
    side: 'left',
    selected_board: null,
    core_boards: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = async () => {
    // Never ask the service worker/network for this endpoint while offline.
    // Sticky Core settings are small account preferences, so localStorage is
    // the offline source of truth until connectivity returns.
    if (!navigator.onLine) {
      const cached = loadStickyCoreCache();
      if (cached) setSettings(cached);
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get('/core-settings.php');
      setSettings(data);
      saveStickyCoreCache(data);
    } catch (err) {
      const cached = loadStickyCoreCache();
      if (cached) setSettings(cached);
      console.debug('Could not load Sticky Core settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isUnlimited) {
      setLoading(false);
      return undefined;
    }
    loadSettings();
    const refresh = () => loadSettings();
    window.addEventListener('sticky-core-changed', refresh);
    return () => window.removeEventListener('sticky-core-changed', refresh);
  }, [isUnlimited]);

  const updateSettings = async (patch) => {
    if (saving) return;
    setSaving(true);

    try {
      const payload = {
        action: 'settings',
        enabled: patch.enabled ?? settings.enabled,
        board_id:
          Object.prototype.hasOwnProperty.call(patch, 'board_id')
            ? patch.board_id
            : settings.board_id,
        side: patch.side ?? settings.side,
      };

      // If Sticky Core is being enabled for the first time, choose the first
      // available Core Board rather than sending an invalid empty selection.
      if (payload.enabled && !payload.board_id && settings.core_boards?.length) {
        payload.board_id = settings.core_boards[0].id;
      }

      // Offline changes are applied locally so AAC navigation remains usable.
      // They can be persisted to MySQL the next time the user changes the
      // setting while online.
      if (!navigator.onLine) {
        const selectedBoard = (settings.core_boards || []).find(
          (board) => String(board.id) === String(payload.board_id)
        ) || null;

        const offlineSettings = {
          ...settings,
          enabled: Boolean(payload.enabled),
          board_id: payload.board_id || null,
          side: payload.side === 'right' ? 'right' : 'left',
          selected_board: selectedBoard,
        };

        setSettings(offlineSettings);
        saveStickyCoreCache(offlineSettings);
        window.dispatchEvent(
          new CustomEvent('sticky-core-changed', { detail: offlineSettings })
        );
        return;
      }

      const { data } = await api.put('/core-settings.php', payload);
      setSettings(data);
      saveStickyCoreCache(data);
      window.dispatchEvent(
        new CustomEvent('sticky-core-changed', { detail: data })
      );
    } catch (err) {
      showToast(
        err?.response?.data?.message ||
          'Could not update Sticky Core settings.',
        'error',
        false
      );
    } finally {
      setSaving(false);
    }
  };

  const coreBoards = Array.isArray(settings?.core_boards)
    ? settings.core_boards
    : [];

      const selectedCoreBoard =
    coreBoards.find((board) => String(board.id) === String(settings?.board_id)) ||
    settings?.selected_board ||
    null;

  const canOpenSelectedCore = Boolean(
    selectedCoreBoard?.id &&
    selectedCoreBoard?.core_default_board_id &&
    typeof onOpen === 'function'
  );

  if (!isUnlimited)   return (
    <div
      style={{
        margin: '0 0 18px',
        padding: '14px 16px',
        border: '1px solid #d9e3ef',
        borderRadius: 10,
        background: '#f7fbff',
        maxWidth: 850,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontWeight: 800, color: '#17324d' }}>Sticky Core</div>
          <div style={{ fontSize: 13, color: '#5f6b7a', marginTop: 2 }}>
            Keep one Core Board visible while you navigate your other boards.
          </div>
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 700,
            cursor: coreBoards.length ? 'pointer' : 'default',
          }}
        >
        <span style={{
              display: 'inline-block',
              padding: '3px 8px',
              borderRadius: 999,
              background: '#e5f4ff',
              color: '#1261a0',
              fontSize: '11px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
        }}>Unlimited</span>
        </label>
      </div>

      {coreBoards.length === 0 ? (
        <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
          No Core Boards yet. Open a board, choose <strong>Board Setup</strong>,
          then enable <strong>Make this a Core Board</strong>.
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'end',
            marginTop: 12,
          }}
        >
          <label style={{ display: 'grid', gap: 5, minWidth: 230, fontSize: 13 }}>
            Active Core Board
            <select
              value={settings?.board_id || ''}
              disabled={saving}
              style={{ padding: 8, borderRadius: 6, border: '1px solid #c8d2df' }}
            >
              <option value="" disabled>Select Core Board</option>
              {coreBoards.map((board) => (
                <option key={board.id} value={board.id}>
                  {board.name}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'grid', gap: 5, minWidth: 140, fontSize: 13 }}>
            Core side
            <select
              value={settings?.side || 'left'}
              disabled={saving}
              style={{ padding: 8, borderRadius: 6, border: '1px solid #c8d2df' }}
            >
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </label>

          {saving && (
            <span style={{ fontSize: 12, color: '#5f6b7a', paddingBottom: 9 }}>
              Saving…
            </span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div
      style={{
        margin: '0 0 18px',
        padding: '14px 16px',
        border: '1px solid #d9e3ef',
        borderRadius: 10,
        background: '#f7fbff',
        maxWidth: 850,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontWeight: 800, color: '#17324d' }}>Sticky Core</div>
          <div style={{ fontSize: 13, color: '#5f6b7a', marginTop: 2 }}>
            Keep one Core Board visible while you navigate your other boards.
          </div>
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 700,
            cursor: coreBoards.length ? 'pointer' : 'default',
          }}
        >
          <input
            type="checkbox"
            checked={Boolean(settings?.enabled)}
            disabled={loading || saving || coreBoards.length === 0}
            onChange={(e) => updateSettings({ enabled: e.target.checked })}
          />
          {settings?.enabled ? 'On' : 'Off'}
        </label>
      </div>

      {coreBoards.length === 0 ? (
        <div style={{ marginTop: 10, fontSize: 13, color: '#6b7280' }}>
          No Core Boards yet. Open a board, choose <strong>Board Setup</strong>,
          then enable <strong>Make this a Core Board</strong>.
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'end',
            marginTop: 12,
          }}
        >
          <label style={{ display: 'grid', gap: 5, minWidth: 230, fontSize: 13 }}>
            Active Core Board
            <select
              value={settings?.board_id || ''}
              disabled={saving}
              onChange={(e) =>
                updateSettings({ board_id: Number(e.target.value) })
              }
              style={{ padding: 8, borderRadius: 6, border: '1px solid #c8d2df' }}
            >
              <option value="" disabled>Select Core Board</option>
              {coreBoards.map((board) => (
                <option key={board.id} value={board.id}>
                  {board.name}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'grid', gap: 5, minWidth: 140, fontSize: 13 }}>
            Core side
            <select
              value={settings?.side || 'left'}
              disabled={saving}
              onChange={(e) => updateSettings({ side: e.target.value })}
              style={{ padding: 8, borderRadius: 6, border: '1px solid #c8d2df' }}
            >
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </label>

          <button
            type="button"
            disabled={!canOpenSelectedCore || saving || loading}
            onClick={() => {
              if (!canOpenSelectedCore) return;
              onOpen(selectedCoreBoard.id);
            }}
            title={
              selectedCoreBoard?.core_default_board_id
                ? `Open ${selectedCoreBoard.name || 'selected Core Board'}`
                : 'Set a default linked board before opening this Core Board.'
            }
            style={{
              padding: '8px 14px',
              border: 'none',
              borderRadius: 6,
              background: canOpenSelectedCore && !saving && !loading ? '#1976d2' : '#a9b9c8',
              color: '#fff',
              fontWeight: 700,
              cursor: canOpenSelectedCore && !saving && !loading ? 'pointer' : 'not-allowed',
              minHeight: 35,
            }}
          >
            Open
          </button>

          {saving && (
            <span style={{ fontSize: 12, color: '#5f6b7a', paddingBottom: 9 }}>
              Saving…
            </span>
          )}
        </div>
      )}
    </div>
  );
}
